import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useEffect } from "react";
import RModal from "react-modal";
import { ButtonSecondary, ButtonPrimary } from "./buttons.js";
import { Clickable } from "./Clickable.js";
import { useUniqueId } from "./hooks/useUniqueId.js";
import { Icon } from "./Icon.js";
import { getPopupRoot } from "./internal/PopupPortal.js";
const modalStyles = {
  overlay: {
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    overflowY: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "2rem 1rem",
    backgroundColor: "rgba(0, 0, 0, 0.5)"
  },
  modal: {
    position: "relative",
    padding: "1.5rem 1.25rem",
    borderRadius: 5,
    backgroundColor: "white",
    boxShadow: "0 0 8px 0 rgba(0,0,0,0.12), 0 8px 8px 0 rgba(0,0,0,0.24)",
    outline: "none"
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    flex: "none",
    marginBottom: "1rem"
  },
  title: {
    overflow: "hidden",
    fontSize: 18,
    fontWeight: 600,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  buttonRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "baseline",
    marginTop: "1rem"
  }
};
RModal.defaultStyles = { overlay: {}, content: {} };
const Modal = (props) => {
  const { cancelText = "Cancel", children, danger = false, okButton, showButtons = true, showCancel = true, showX = false, styles, title, titleChildren, width = 450, onDismiss, ...otherProps } = props;
  const hasTitle = !!title;
  const titleId = useUniqueId();
  const modalElement = useRef(null);
  const previouslyFocusedElement = useRef(null);
  useEffect(() => {
    previouslyFocusedElement.current = document.activeElement;
    return () => {
      if (previouslyFocusedElement.current instanceof HTMLElement) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, []);
  return jsxs(RModal, {
    aria: {
      labelledby: hasTitle ? titleId : void 0,
      modal: true
    },
    // Adding aria-hidden to the app container is unnecessary with aria-modal set on the modal.
    // See https://github.com/DataBiosphere/terra-ui/pull/2541
    ariaHideApp: false,
    contentRef: (node) => {
      modalElement.current = node;
    },
    isOpen: true,
    parentSelector: getPopupRoot,
    // Customize focus handling using onAfterOpen to properly manage focus where
    // react-modal and react-focus-lock interact.
    // See https://github.com/DataBiosphere/terra-ui/pull/1938
    shouldFocusAfterRender: false,
    shouldReturnFocusAfterClose: false,
    style: {
      overlay: modalStyles.overlay,
      content: {
        ...modalStyles.modal,
        width,
        ...styles == null ? void 0 : styles.modal
      }
    },
    onAfterOpen: () => {
      const nodeToFocus = modalElement.current.contains(document.activeElement) ? document.activeElement : modalElement.current;
      setTimeout(() => {
        var _a;
        previouslyFocusedElement.current = ((_a = modalElement.current) == null ? void 0 : _a.contains(document.activeElement)) ? previouslyFocusedElement.current : document.activeElement;
        const isModalMounted = modalElement.current !== null;
        if (isModalMounted) {
          const isFocusWithinModal = !!document.activeElement && (modalElement.current === document.activeElement || modalElement.current.contains(document.activeElement));
          if (!isFocusWithinModal && document.contains(nodeToFocus) && nodeToFocus instanceof HTMLElement) {
            nodeToFocus.focus();
          }
        }
      }, 0);
    },
    onRequestClose: onDismiss,
    ...otherProps,
    children: [hasTitle && jsxs("div", { style: modalStyles.header, children: [jsx("div", { id: titleId, style: modalStyles.title, children: title }), titleChildren, showX && jsx(Clickable, { "aria-label": "Close modal", style: { alignSelf: "flex-start", marginLeft: "auto" }, onClick: onDismiss, children: jsx(Icon, { icon: "times-circle" }) })] }), children, showButtons && jsxs("div", { style: { ...modalStyles.buttonRow, ...styles == null ? void 0 : styles.buttonRow }, children: [showCancel && jsx(ButtonSecondary, { style: { marginRight: "1rem" }, onClick: onDismiss, children: cancelText }), (() => {
      if (okButton === void 0) {
        return jsx(ButtonPrimary, { danger, onClick: onDismiss, children: "OK" });
      }
      if (typeof okButton === "string") {
        return jsx(ButtonPrimary, { danger, onClick: onDismiss, children: okButton });
      }
      if (typeof okButton === "function") {
        return jsx(ButtonPrimary, { danger, onClick: okButton, children: "OK" });
      }
      return okButton;
    })()] })]
  });
};
export {
  Modal,
  modalStyles
};
